import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "mimes-cookie-consent-v1";

export const CONSENT_EVENT = "mimes-consent-changed";

export function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch { return null; }
}

function saveConsent(consent) {
  const value = { ...consent, timestamp: new Date().toISOString(), version: 1 };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  return value;
}

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent("mimes-open-cookie-prefs"));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
    const openHandler = () => {
      const cur = getConsent();
      setAnalytics(cur?.analytics ?? false);
      setPrefsOpen(true);
    };
    window.addEventListener("mimes-open-cookie-prefs", openHandler);
    return () => window.removeEventListener("mimes-open-cookie-prefs", openHandler);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: true });
    setVisible(false); setPrefsOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: false });
    setVisible(false); setPrefsOpen(false);
  }, []);

  const savePrefs = useCallback(() => {
    saveConsent({ necessary: true, analytics });
    setVisible(false); setPrefsOpen(false);
  }, [analytics]);

  return (
    <>
      {visible && (
        <div
          data-testid="cookie-banner"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          className="fixed bottom-0 inset-x-0 z-50 bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)] shadow-2xl border-t border-[color:var(--brand-bg)]/10"
        >
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 shrink-0 mt-0.5 text-[color:var(--brand-terracotta)]" strokeWidth={1.5} />
              <div>
                <p id="cookie-banner-title" className="font-serif text-lg mb-1">Cuidamos tu privacidad</p>
                <p className="text-sm text-[color:var(--brand-bg)]/85 leading-relaxed">
                  Utilizamos cookies técnicas (necesarias para el carrito) y cookies analíticas anónimas (Google Analytics) para mejorar la tienda.
                  Puedes aceptar, rechazar o configurar. Más info en nuestra <Link to="/cookies" className="underline hover:text-[color:var(--brand-terracotta)]">política de cookies</Link>.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0 w-full lg:w-auto">
              <Button
                data-testid="cookie-reject"
                variant="outline"
                onClick={rejectAll}
                className="bg-transparent border-[color:var(--brand-bg)]/40 text-[color:var(--brand-bg)] hover:bg-white/10 hover:text-[color:var(--brand-bg)]"
              >
                Solo necesarias
              </Button>
              <Button
                data-testid="cookie-configure"
                variant="outline"
                onClick={() => { setAnalytics(false); setPrefsOpen(true); }}
                className="bg-transparent border-[color:var(--brand-bg)]/40 text-[color:var(--brand-bg)] hover:bg-white/10 hover:text-[color:var(--brand-bg)]"
              >
                Configurar
              </Button>
              <Button
                data-testid="cookie-accept"
                variant="outline"
                onClick={acceptAll}
                className="bg-transparent border-[color:var(--brand-bg)]/40 text-[color:var(--brand-bg)] hover:bg-white/10 hover:text-[color:var(--brand-bg)]"
              >
                Aceptar todas
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent data-testid="cookie-prefs-dialog" className="max-w-lg bg-[color:var(--brand-bg)] border-[color:var(--brand-border)]">
          <DialogTitle className="font-serif text-2xl text-[color:var(--brand-primary)]">Configurar cookies</DialogTitle>
          <DialogDescription className="text-[color:var(--brand-neutral)]">
            Elige qué tipos de cookies quieres permitir. Puedes cambiar tu decisión en cualquier momento desde el pie de página.
          </DialogDescription>

          <div className="space-y-4 mt-4">
            <div className="border border-[color:var(--brand-border)] rounded-md p-4 bg-[color:var(--brand-bg-alt)]/40">
              <div className="flex items-center justify-between mb-2">
                <p className="font-serif text-lg text-[color:var(--brand-primary)]">Cookies necesarias</p>
                <span className="text-xs uppercase tracking-widest text-[color:var(--brand-sage)] font-semibold">Siempre activas</span>
              </div>
              <p className="text-sm text-[color:var(--brand-neutral)] leading-relaxed">
                Imprescindibles para el funcionamiento de la web: carrito de compra, sesión de usuario, preferencias de idioma. No requieren consentimiento.
              </p>
            </div>

            <div className="border border-[color:var(--brand-border)] rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-serif text-lg text-[color:var(--brand-primary)]">Cookies analíticas</p>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    data-testid="cookie-toggle-analytics"
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-[color:var(--brand-border)] rounded-full peer-checked:bg-[color:var(--brand-sage)] transition-colors relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                </label>
              </div>
              <p className="text-sm text-[color:var(--brand-neutral)] leading-relaxed">
                Google Analytics 4 con IP anonimizada. Nos ayudan a entender qué páginas gustan más y mejorar la tienda. No comparten datos personales.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-4 border-t border-[color:var(--brand-border)] mt-4">
            <Button variant="outline" onClick={rejectAll} data-testid="cookie-prefs-reject">
              Rechazar todas
            </Button>
            <Button onClick={savePrefs} data-testid="cookie-prefs-save" className="bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)] hover:opacity-90">
              Guardar preferencias
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
